import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Stack, Card, Alert, Placeholder, Button } from 'react-bootstrap';
import { Link, Outlet, useSearchParams } from 'react-router-dom';
import { PaginationState } from '@tanstack/react-table';

import useInvoices from 'app/hooks/useInvoices';
import InvoiceFilters from './InvoiceFilters';
import InvoiceTableCard from './InvoiceTableCard';
import { paths } from 'app/paths';

const DEFAULT_PAGE_SIZE = 10;

const InvoiceTable = () => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [searchParams] = useSearchParams();

  // Memoized string of current filters
  const filtersKey = useMemo(() => {
    const keys = [
      'customer_id',
      'status',
      'paid',
      'finalized',
      'date_from',
      'date_to',
    ];
    const filterObject = Object.fromEntries(
      keys.map((key) => [key, searchParams.get(key)])
    );
    return JSON.stringify(filterObject);
  }, [searchParams]);

  // Reset pageIndex to 0 when any filter changes
  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  }, [filtersKey]);

  const { data, error, isLoading } = useInvoices(pagination);
  const invoices = data?.invoices ?? [];
  const totalEntries = data?.pagination?.total_entries ?? 0;

  return (
    <>
      <Helmet>
        <title>{`Invoices (${totalEntries})`}</title>
      </Helmet>

      <Stack direction="horizontal" gap={4} className="justify-content-center">
        <Card className="bg-light shadow-sm border-0 w-100 mt-5 mb-3 px-3">
          <Card.Body className="p-0">
            <Stack direction="horizontal" gap={3} className="p-3">
              <h4 className="mb-0">Invoices List</h4>
              <div className="ms-auto">
                <Link to={`${paths.invoices.new()}?${searchParams.toString()}`}>
                  <Button variant="primary">New invoice</Button>
                </Link>
              </div>
            </Stack>

            <InvoiceFilters />

            {error && (
              <Alert variant="warning" className="m-3">
                There was an error loading the invoices. Please try again later.
              </Alert>
            )}

            {isLoading && (
              <Placeholder
                data-testid="loading-placeholder"
                as="div"
                animation="glow"
                className="m-3"
              >
                {[...Array(3)].map((_, i) => (
                  <Placeholder key={i} xs={12} className="mb-2" />
                ))}
              </Placeholder>
            )}

            {!isLoading && !error && (
              <InvoiceTableCard
                invoices={invoices}
                totalEntries={totalEntries}
                pagination={pagination}
                setPagination={setPagination}
              />
            )}
          </Card.Body>
        </Card>
      </Stack>
      <Outlet />
    </>
  );
};

export default InvoiceTable;
